import { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { format } from 'date-fns';
import {
    Inbox, Map, Heart, UserCircle, Plus, MapPin, Calendar,
    ChevronRight, ChevronLeft, LogOut, Clock, Check, Pencil,
    Star, ArrowRight, Users, Send, Compass, Sunrise, Globe,
    Shield, FileText, Mail, Phone, Flag, X, LayoutList,
    Columns2, BookOpen, Copy, CircleCheck, AlertCircle, BadgeCheck, Upload,
    Loader2, ArrowLeftRight, DollarSign, ChevronDown, ChevronUp,
    MessageCircle, Sparkles, CalendarDays, Eye, Building2, ExternalLink
} from 'lucide-react';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Camera } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const NATIONALITIES = [
    'Afghan','Albanian','Algerian','American','Andorran','Angolan','Argentine','Armenian','Australian',
    'Austrian','Azerbaijani','Bahamian','Bahraini','Bangladeshi','Barbadian','Belarusian','Belgian',
    'Belizean','Beninese','Bhutanese','Bolivian','Bosnian','Botswanan','Brazilian','British','Bruneian',
    'Bulgarian','Burkinabe','Burmese','Burundian','Cambodian','Cameroonian','Canadian','Cape Verdean',
    'Central African','Chadian','Chilean','Chinese','Colombian','Comorian','Congolese','Costa Rican',
    'Croatian','Cuban','Cypriot','Czech','Danish','Djiboutian','Dominican','Dutch','Ecuadorean','Egyptian',
    'Emirati','English','Equatorial Guinean','Eritrean','Estonian','Ethiopian','Fijian','Filipino','Finnish',
    'French','Gabonese','Gambian','Georgian','German','Ghanaian','Greek','Grenadian','Guatemalan','Guinean',
    'Guyanese','Haitian','Honduran','Hungarian','Icelandic','Indian','Indonesian','Iranian','Iraqi','Irish',
    'Israeli','Italian','Ivorian','Jamaican','Japanese','Jordanian','Kazakh','Kenyan','Kuwaiti','Kyrgyz',
    'Laotian','Latvian','Lebanese','Liberian','Libyan','Lithuanian','Luxembourgish','Macedonian','Malagasy',
    'Malawian','Malaysian','Maldivian','Malian','Maltese','Mauritanian','Mauritian','Mexican','Moldovan',
    'Mongolian','Montenegrin','Moroccan','Mozambican','Namibian','Nepalese','New Zealand','Nicaraguan',
    'Nigerian','Norwegian','Omani','Pakistani','Palestinian','Panamanian','Papua New Guinean','Paraguayan',
    'Peruvian','Polish','Portuguese','Qatari','Romanian','Russian','Rwandan','Saudi','Scottish','Senegalese',
    'Serbian','Sierra Leonean','Singaporean','Slovak','Slovenian','Somali','South African','South Korean',
    'Spanish','Sri Lankan','Sudanese','Surinamese','Swedish','Swiss','Syrian','Taiwanese','Tajik','Tanzanian',
    'Thai','Togolese','Trinidadian','Tunisian','Turkish','Turkmen','Ugandan','Ukrainian','Uruguayan',
    'Uzbek','Venezuelan','Vietnamese','Welsh','Yemeni','Zambian','Zimbabwean'
];

const statusColors = {
    pending: 'bg-neutral-100 text-neutral-600',
    broadcasted: 'bg-blue-50 text-blue-600',
    responded: 'bg-emerald-50 text-emerald-600',
    booked: 'bg-neutral-900 text-white'
};

const SHOWCASE = [
    { id: 's1', title: 'Serengeti Migration', image: 'https://images.unsplash.com/photo-1516426122078-c23e76319801?w=800', tag: 'Wildlife', days: 5, rating: 4.97, price: 650 },
    { id: 's2', title: 'Zanzibar Beaches', image: 'https://images.unsplash.com/photo-1590523741831-ab7e8b8f9c7f?w=800', tag: 'Beach', days: 4, rating: 4.92, price: 480 },
    { id: 's3', title: 'Kilimanjaro Trek', image: 'https://images.unsplash.com/photo-1621414050946-1b936a78491f?w=800', tag: 'Adventure', days: 7, rating: 4.98, price: 2200 },
    { id: 's4', title: 'Ngorongoro Crater', image: 'https://images.unsplash.com/photo-1489392191049-fc10c97e64b6?w=800', tag: 'Safari', days: 3, rating: 4.95, price: 890 },
    { id: 's5', title: 'Maasai Cultural Tour', image: 'https://images.unsplash.com/photo-1630067458414-0080622bc0df?w=800', tag: 'Culture', days: 3, rating: 4.93, price: 420 },
    { id: 's6', title: 'Lake Manyara', image: 'https://images.unsplash.com/photo-1547970810-dc1eac37d174?w=800', tag: 'Nature', days: 2, rating: 4.88, price: 350 }
];

const CATEGORIES = [
    { id: 'all', label: 'All', icon: Globe },
    { id: 'Wildlife', label: 'Wildlife', icon: Compass },
    { id: 'Beach', label: 'Beach', icon: Sunrise },
    { id: 'Adventure', label: 'Adventure', icon: MapPin },
    { id: 'Safari', label: 'Safari', icon: Map },
    { id: 'Culture', label: 'Culture', icon: Star }
];

function getGreeting() {
    const h = new Date().getHours();
    if (h < 12) return 'Good morning';
    if (h < 17) return 'Good afternoon';
    return 'Good evening';
}

export default function TravelerDashboard() {
    const [activeView, setActiveView] = useState('experiences');
    const [trips, setTrips] = useState([]);
    const [selectedTrip, setSelectedTrip] = useState(null);
    const [itineraries, setItineraries] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadingItineraries, setLoadingItineraries] = useState(false);
    const [activeCategory, setActiveCategory] = useState('all');
    const [bucketlist, setBucketlist] = useState(() => {
        try { return JSON.parse(localStorage.getItem('karibu_bucketlist') || '[]'); } catch { return []; }
    });
    const [profile, setProfile] = useState(null);
    const [editing, setEditing] = useState(false);
    const [editForm, setEditForm] = useState({});
    const [savingProfile, setSavingProfile] = useState(false);
    const [uploadingAvatar, setUploadingAvatar] = useState(false);
    const [uploadingId, setUploadingId] = useState(false);
    const [nationalitySearch, setNationalitySearch] = useState('');
    const [compareMode, setCompareMode] = useState(false);
    const [compareItems, setCompareItems] = useState([]);
    const [bookings, setBookings] = useState([]);
    const [selectedBooking, setSelectedBooking] = useState(null);
    const [showBookingDialog, setShowBookingDialog] = useState(false);
    const [bookingItinerary, setBookingItinerary] = useState(null);
    const [operatorPreview, setOperatorPreview] = useState(null);
    const [bookingNotes, setBookingNotes] = useState('');
    const [bookingSubmitting, setBookingSubmitting] = useState(false);
    const [reviewDialog, setReviewDialog] = useState(null);
    const [reviewRating, setReviewRating] = useState(5);
    const [reviewComment, setReviewComment] = useState('');
    const [reviewSubmitting, setReviewSubmitting] = useState(false);
    const [expandedDays, setExpandedDays] = useState({});
    const [conversations, setConversations] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [activeConversation, setActiveConversation] = useState(null);
    const [messages, setMessages] = useState([]);
    const [newMessage, setNewMessage] = useState('');
    const [sendingMsg, setSendingMsg] = useState(false);
    const [tripTab, setTripTab] = useState('all');
    const wsRef = useRef(null);
    const messagesEndRef = useRef(null);
    const { user, logout, api } = useAuth();
    const navigate = useNavigate();

    const fetchConversations = useCallback(async () => {
        try {
            const [convRes, unreadRes] = await Promise.all([
                api.get('/conversations'),
                api.get('/conversations/unread-count')
            ]);
            setConversations(convRes.data);
            setUnreadCount(unreadRes.data.total_unread || 0);
        } catch { /* empty */ }
    }, [api]);

    useEffect(() => { fetchTrips(); fetchProfile(); fetchBookings(); fetchConversations(); }, []);
    useEffect(() => { const interval = setInterval(fetchConversations, 15000); return () => clearInterval(interval); }, [fetchConversations]);
    useEffect(() => { localStorage.setItem('karibu_bucketlist', JSON.stringify(bucketlist)); }, [bucketlist]);

    const fetchProfile = async () => {
        try { const r = await api.get('/profile'); setProfile(r.data); }
        catch { /* use user from auth */ }
    };

    const fetchBookings = async () => {
        try { const r = await api.get('/bookings'); setBookings(r.data); }
        catch { /* empty */ }
    };

    const startEditing = () => {
        setEditForm({
            name: profile?.name || user?.name || '',
            email: profile?.email || user?.email || '',
            nationality: profile?.nationality || '',
            bio: profile?.bio || '',
            id_document: profile?.id_document || ''
        });
        setEditing(true);
    };

    const saveProfile = async () => {
        setSavingProfile(true);
        try {
            const r = await api.put('/profile', editForm);
            setProfile(r.data);
            setEditing(false);
            toast.success('Profile updated');
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Failed to save');
        } finally { setSavingProfile(false); }
    };

    const uploadAvatar = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
            toast.error('Please select a JPEG, PNG, or WebP image');
            return;
        }
        if (file.size > 5 * 1024 * 1024) { toast.error('Image too large (max 5MB)'); return; }

        setUploadingAvatar(true);
        try {
            const formData = new FormData();
            formData.append('file', file);
            const r = await api.post('/profile/avatar', formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            setProfile(p => ({ ...p, avatar_path: r.data.path }));
            toast.success('Photo updated');
        } catch (e) {
            toast.error('Upload failed');
        } finally { setUploadingAvatar(false); }
    };

    const uploadIdDocument = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (!['image/jpeg', 'image/png', 'image/webp', 'application/pdf'].includes(file.type)) {
            toast.error('Please select a JPEG, PNG, WebP, or PDF file');
            return;
        }
        if (file.size > 10 * 1024 * 1024) { toast.error('File too large (max 10MB)'); return; }

        setUploadingId(true);
        try {
            const formData = new FormData();
            formData.append('file', file);
            const r = await api.post('/profile/id-document', formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            setProfile(p => ({ ...p, id_document: r.data.path, is_verified: true }));
            toast.success('ID document uploaded — you are now verified!');
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Upload failed');
        } finally { setUploadingId(false); }
    };

    const getIdDocUrl = () => {
        if (!profile?.id_document) return null;
        const token = localStorage.getItem('karibu_token');
        return `${API_URL}/api/files/${profile.id_document}?auth=${token}`;
    };

    const getAvatarUrl = () => {
        if (!profile?.avatar_path) return null;
        const token = localStorage.getItem('karibu_token');
        return `${API_URL}/api/files/${profile.avatar_path}?auth=${token}`;
    };

    const fetchTrips = async () => {
        try { const r = await api.get('/trips'); setTrips(r.data); }
        catch { /* empty */ }
        finally { setLoading(false); }
    };

    const loadItineraries = async (tripId) => {
        setLoadingItineraries(true);
        try { const r = await api.get(`/itineraries/trip/${tripId}`); setItineraries(r.data); }
        catch { setItineraries([]); }
        finally { setLoadingItineraries(false); }
    };

    const selectItinerary = async (itineraryId) => {
        try {
            await api.patch(`/itineraries/${itineraryId}/select`);
            toast.success('Itinerary selected!');
            fetchTrips();
            fetchBookings();
            if (selectedTrip) loadItineraries(selectedTrip.id);
        } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    };

    const previewOperator = async (operatorId) => {
        try {
            const res = await api.get(`/profile/operator/${operatorId}`);
            setOperatorPreview(res.data);
        } catch { toast.error('Could not load operator profile'); }
    };


    const openBookingFlow = (itinerary) => {
        setBookingItinerary(itinerary);
        setBookingNotes('');
        setShowBookingDialog(true);
    };

    const submitBooking = async () => {
        if (!bookingItinerary) return;
        setBookingSubmitting(true);
        try {
            const res = await api.post('/bookings', {
                itinerary_id: bookingItinerary.id,
                traveler_notes: bookingNotes || null
            });
            toast.success(`Booking confirmed! Ref: ${res.data.booking_ref}`);
            setShowBookingDialog(false);
            fetchTrips();
            fetchBookings();
            if (selectedTrip) loadItineraries(selectedTrip.id);
            setSelectedBooking(res.data);
            setActiveView('booking-success');
        } catch (e) { toast.error(e.response?.data?.detail || 'Booking failed'); }
        finally { setBookingSubmitting(false); }
    };

    const cancelBooking = async (bookingId) => {
        try {
            await api.patch(`/bookings/${bookingId}/cancel`);
            toast.success('Booking cancelled');
            fetchBookings();
            fetchTrips();
        } catch (e) { toast.error(e.response?.data?.detail || 'Failed to cancel'); }
    };

    const submitReview = async () => {
        if (!reviewDialog) return;
        setReviewSubmitting(true);
        try {
            await api.post('/reviews', { booking_id: reviewDialog.id, rating: reviewRating, comment: reviewComment });
            toast.success('Review submitted!');
            setReviewDialog(null);
            setReviewRating(5);
            setReviewComment('');
        } catch (e) { toast.error(e.response?.data?.detail || 'Failed to submit review'); }
        finally { setReviewSubmitting(false); }
    };

    const toggleCompare = (itinerary) => {
        setCompareItems(prev => {
            const exists = prev.find(c => c.id === itinerary.id);
            if (exists) return prev.filter(c => c.id !== itinerary.id);
            if (prev.length >= 3) { toast.error('Compare up to 3 itineraries'); return prev; }
            return [...prev, itinerary];
        });
    };

    const toggleDayExpand = (itinId, dayNum) => {
        const key = `${itinId}-${dayNum}`;
        setExpandedDays(prev => ({ ...prev, [key]: !prev[key] }));
    };

    const openTrip = (trip) => { setSelectedTrip(trip); loadItineraries(trip.id); setActiveView('trip-detail'); };
    const toggleBucketlist = (item) => {
        setBucketlist(prev => prev.find(b => b.id === item.id) ? prev.filter(b => b.id !== item.id) : [...prev, item]);
    };
    const isSaved = (id) => bucketlist.some(b => b.id === id);
    const handleLogout = () => { logout(); navigate('/'); };
    const formatDate = (d) => { try { return format(new Date(d), 'MMM dd'); } catch { return d || ''; } };
    const inboxCount = trips.reduce((a, t) => a + (t.itinerary_count || 0), 0);
    const filteredShowcase = activeCategory === 'all' ? SHOWCASE : SHOWCASE.filter(s => s.tag === activeCategory);

    // ====== MESSAGING ======
    const openConversation = async (convo) => {
        setActiveConversation(convo);
        try {
            const res = await api.get(`/conversations/${convo.id}/messages`);
            setMessages(res.data);
            await api.post(`/conversations/${convo.id}/read`);
            fetchConversations();
        } catch { toast.error('Failed to load messages'); }
        
        // Connect WebSocket
        if (wsRef.current) { wsRef.current.close(); }
        const token = localStorage.getItem('karibu_token');
        const wsUrl = API_URL.replace('https://', 'wss://').replace('http://', 'ws://');
        const ws = new WebSocket(`${wsUrl}/ws/chat/${convo.id}?token=${token}`);
        ws.onmessage = (event) => {
            const msg = JSON.parse(event.data);
            setMessages(prev => {
                if (prev.find(m => m.id === msg.id)) return prev;
                return [...prev, msg];
            });
        };
        wsRef.current = ws;
    };

    useEffect(() => {
        if (messagesEndRef.current) messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }, [messages]);

    useEffect(() => {
        return () => { if (wsRef.current) wsRef.current.close(); };
    }, []);

    const sendMessage = async () => {
        if (!newMessage.trim() || !activeConversation) return;
        setSendingMsg(true);
        try {
            if (wsRef.current?.readyState === WebSocket.OPEN) {
                wsRef.current.send(JSON.stringify({ content: newMessage.trim() }));
            } else {
                await api.post(`/conversations/${activeConversation.id}/messages`, { content: newMessage.trim() });
                const res = await api.get(`/conversations/${activeConversation.id}/messages`);
                setMessages(res.data);
            }
            setNewMessage('');
        } catch { toast.error('Failed to send'); }
        finally { setSendingMsg(false); }
    };

    const formatTime = (d) => { try { return format(new Date(d), 'h:mm a'); } catch { return ''; } };

    const NAV_ITEMS = [
        { id: 'experiences', label: 'Explore', icon: Compass, count: null, desc: 'Browse experiences' },
        { id: 'trips', label: 'Trips', icon: Map, count: trips.length + bookings.filter(b => b.status === 'confirmed').length, desc: 'Requests & bookings' },
        { id: 'messages', label: 'Messages', icon: MessageCircle, count: unreadCount, desc: 'Chat with operators' },
        { id: 'calendar', label: 'Calendar', icon: CalendarDays, count: null, desc: 'Upcoming dates' },
        { id: 'profile', label: 'Profile', icon: UserCircle, count: null, desc: 'Your account' }
    ];

    if (loading) return <div className="min-h-screen bg-white flex items-center justify-center"><div className="spinner" /></div>;

    return (
        <div className="min-h-screen bg-white">
            {/* Header — Airbnb-clean */}
            <header className="sticky top-0 z-50 bg-white border-b border-neutral-100">
                <div className="max-w-[1280px] mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
                    <div className="flex items-center gap-6">
                        <Link to="/" className="flex items-center" data-testid="dash-brand">
                            <img src="/safarinodes-logo-dark.png" alt="safarinodes" className="h-9 w-auto" />
                        </Link>
                        {activeView !== 'home' && (
                            <button onClick={() => setActiveView('home')} className="hidden sm:flex items-center gap-1 text-sm text-neutral-500 hover:text-neutral-900 transition" data-testid="dash-back-home">
                                <ChevronLeft size={16} /> Home
                            </button>
                        )}
                    </div>

                    {/* Center search bar removed */}

                    <div className="flex items-center gap-2">
                        <Button
                            onClick={() => navigate('/trip/create')}
                            className="hidden sm:flex items-center gap-1.5 bg-transparent border border-neutral-200 text-neutral-700 hover:bg-neutral-50 rounded-full px-4 py-2 text-sm font-medium"
                            data-testid="create-trip-btn"
                        >
                            <Plus size={15} /> Create trip
                        </Button>
                        <button
                            onClick={() => setActiveView(activeView === 'profile' ? 'home' : 'profile')}
                            className="relative flex items-center gap-2 border border-neutral-200 rounded-full px-2 py-1.5 hover:shadow-md transition-shadow"
                            data-testid="user-menu-btn"
                        >
                            {getAvatarUrl() ? (
                                <img src={getAvatarUrl()} alt="" className="w-7 h-7 rounded-full object-cover" />
                            ) : (
                                <div className="w-7 h-7 rounded-full bg-neutral-700 flex items-center justify-center text-white text-xs font-semibold">
                                    {user?.name?.charAt(0)?.toUpperCase() || 'T'}
                                </div>
                            )}
                            {profile?.is_verified && (
                                <BadgeCheck size={14} className="absolute -top-1 -right-1 text-[#073763] bg-white rounded-full" data-testid="header-verified-badge" />
                            )}
                        </button>
                    </div>
                </div>
            </header>

            <main className="max-w-[1280px] mx-auto px-4 sm:px-6">
                {/* Greeting */}
                <div className="pt-6 pb-4">
                    <h1 className="text-2xl font-semibold text-neutral-900" data-testid="dash-welcome">
                        {getGreeting()}, {(user?.name || 'Traveler').split(' ')[0]}
                    </h1>
                </div>

                {/* Square Card Grid — always visible */}
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 pb-6" data-testid="dash-boxes">
                    {NAV_ITEMS.map(item => (
                        <button
                            key={item.id}
                            onClick={() => setActiveView(activeView === item.id ? 'home' : item.id)}
                            className={`group relative rounded-2xl p-4 border text-left transition-all duration-200 active:scale-[0.97] ${
                                activeView === item.id
                                    ? 'bg-neutral-900 text-white border-neutral-900 shadow-lg'
                                    : 'bg-white text-neutral-700 border-neutral-200 hover:border-neutral-300 hover:shadow-md'
                            }`}
                            data-testid={`dash-box-${item.id}`}
                        >
                            <div className="flex items-center justify-between mb-2.5">
                                <item.icon size={22} className={`transition-colors ${activeView === item.id ? 'text-white' : 'text-neutral-600 group-hover:text-[#073763]'}`} strokeWidth={1.5} />
                                {item.count > 0 && (
                                    <span className={`min-w-[20px] h-5 px-1.5 text-[11px] font-bold rounded-full flex items-center justify-center ${
                                        activeView === item.id ? 'bg-white/20 text-white' : 'bg-[#073763] text-white'
                                    }`}>
                                        {item.count}
                                    </span>
                                )}
                            </div>
                            <h3 className={`font-medium text-[14px] ${activeView === item.id ? 'text-white' : 'text-neutral-900'}`}>{item.label}</h3>
                            <p className={`text-[12px] mt-0.5 ${activeView === item.id ? 'text-white/60' : 'text-neutral-400'}`}>{item.desc}</p>
                        </button>
                    ))}
                </div>

                {/* ===== HOME (default) ===== */}
                {activeView === 'home' && (
                    <div className="py-4">

                        {/* Active Trips preview */}
                        {trips.length > 0 && (
                            <div className="mb-10">
                                <div className="flex items-center justify-between mb-5">
                                    <h2 className="text-lg font-semibold text-neutral-900">Your trips</h2>
                                    <button onClick={() => setActiveView('trips')} className="text-sm font-medium text-neutral-600 hover:text-neutral-900 underline underline-offset-2">
                                        Show all
                                    </button>
                                </div>
                                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                                    {trips.slice(0, 3).map(trip => (
                                        <TripCard key={trip.id} trip={trip} onClick={() => openTrip(trip)} formatDate={formatDate} />
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Quick link to Experiences */}
                        <div className="border border-neutral-200 rounded-xl p-5 mb-6 flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                <Compass size={24} className="text-[#073763]" />
                                <div>
                                    <p className="font-medium text-neutral-900 text-sm">Browse Experiences</p>
                                    <p className="text-xs text-neutral-400">Wildlife, beaches, mountains & more</p>
                                </div>
                            </div>
                            <Button onClick={() => setActiveView('experiences')} variant="ghost" size="sm" className="text-[#073763]">
                                Explore <ChevronRight size={14} className="ml-1" />
                            </Button>
                        </div>

                        {/* Bottom CTA */}
                        <div className="text-center py-10 border-t border-neutral-100">
                            <p className="text-neutral-500 text-sm mb-4">Can't find the perfect trip?</p>
                            <Button
                                onClick={() => navigate('/trip/create')}
                                className="bg-neutral-900 text-white hover:bg-neutral-800 rounded-lg px-6 py-3 font-medium"
                                data-testid="bottom-create-trip"
                            >
                                Create a custom trip request
                            </Button>
                        </div>
                    </div>
                )}

                {/* ===== EXPERIENCES ===== */}
                {activeView === 'experiences' && (
                    <div className="py-4 overflow-x-hidden">
                        {/* Category filter bar */}
                        <div className="border-b border-neutral-100 mb-6 -mx-6 px-6">
                            <div className="flex gap-6 overflow-x-auto pb-3 scrollbar-hide">
                                {CATEGORIES.map(cat => (
                                    <button
                                        key={cat.id}
                                        onClick={() => setActiveCategory(cat.id)}
                                        className={`flex flex-col items-center gap-1.5 pb-2 min-w-[56px] border-b-2 transition-all ${
                                            activeCategory === cat.id
                                                ? 'border-neutral-900 text-neutral-900'
                                                : 'border-transparent text-neutral-400 hover:text-neutral-600 hover:border-neutral-200'
                                        }`}
                                        data-testid={`cat-${cat.id}`}
                                    >
                                        <cat.icon size={22} strokeWidth={1.5} />
                                        <span className="text-xs font-medium whitespace-nowrap">{cat.label}</span>
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Showcase grid */}
                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-6 gap-y-8 mb-12">
                            {filteredShowcase.map(item => (
                                <div key={item.id} className="group" data-testid={`showcase-${item.id}`}>
                                    <div className="relative aspect-square rounded-xl overflow-hidden mb-3">
                                        <img
                                            src={item.image}
                                            alt={item.title}
                                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                                        />
                                        <button
                                            onClick={(e) => { e.stopPropagation(); toggleBucketlist(item); }}
                                            className="absolute top-3 right-3 p-1.5"
                                            data-testid={`save-${item.id}`}
                                        >
                                            <Heart
                                                size={22}
                                                className={`drop-shadow-md transition-colors ${
                                                    isSaved(item.id) ? 'text-red-500 fill-red-500' : 'text-white fill-white/40 hover:fill-white/60'
                                                }`}
                                                strokeWidth={2}
                                            />
                                        </button>
                                        {item.rating >= 4.95 && (
                                            <div className="absolute top-3 left-3 bg-white rounded-full px-2.5 py-1 text-xs font-semibold text-neutral-900 shadow-sm">
                                                Guest favorite
                                            </div>
                                        )}
                                    </div>
                                    <button onClick={() => navigate('/experiences')} className="w-full text-left">
                                        <div className="flex items-start justify-between gap-2">
                                            <h3 className="font-medium text-[15px] text-neutral-900">{item.title}</h3>
                                            <span className="flex items-center gap-0.5 text-sm text-neutral-900 shrink-0">
                                                <Star size={13} className="fill-neutral-900" /> {item.rating}
                                            </span>
                                        </div>
                                        <p className="text-sm text-neutral-500 mt-0.5">{item.tag} &middot; {item.days} days</p>
                                        <p className="text-[15px] text-neutral-900 mt-1"><span className="font-semibold">${item.price}</span> <span className="text-neutral-500 font-normal">/ person</span></p>
                                    </button>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* ===== MESSAGES ===== */}
                {activeView === 'messages' && (
                    <div className="py-4" data-testid="messages-view">
                        {/* WhatsApp-style split view */}
                        <div className="flex rounded-2xl border border-neutral-200 bg-white overflow-hidden" style={{ height: 'calc(100vh - 220px)' }}>
                            {/* Left: Conversation List — hidden on mobile when chat is open */}
                            <div className={`${activeConversation ? 'hidden md:flex' : 'flex'} w-full md:w-[320px] border-r border-neutral-200 flex-col shrink-0`}>
                                <div className="p-4 border-b border-neutral-100">
                                    <h2 className="text-base font-semibold text-neutral-900">Messages</h2>
                                    <p className="text-xs text-neutral-400 mt-0.5">{conversations.length} conversation{conversations.length !== 1 ? 's' : ''}</p>
                                </div>
                                <div className="flex-1 overflow-y-auto">
                                    {conversations.length === 0 ? (
                                        <div className="text-center py-16 px-4">
                                            <MessageCircle className="mx-auto mb-3 text-neutral-200" size={32} />
                                            <p className="text-sm text-neutral-400">No conversations yet</p>
                                            <p className="text-xs text-neutral-300 mt-1">Conversations start when operators send you proposals.</p>
                                        </div>
                                    ) : (
                                        conversations.map(convo => (
                                            <button
                                                key={convo.id}
                                                onClick={() => openConversation(convo)}
                                                className={`w-full p-4 text-left flex items-center gap-3 transition-colors border-b border-neutral-50 ${
                                                    activeConversation?.id === convo.id ? 'bg-neutral-50' : 'hover:bg-neutral-50/50'
                                                }`}
                                                data-testid={`convo-${convo.id}`}
                                            >
                                                <div className="relative shrink-0">
                                                    <div className="w-11 h-11 rounded-2xl bg-neutral-900 flex items-center justify-center text-white text-sm font-bold">
                                                        {convo.operator_name?.charAt(0)?.toUpperCase() || 'O'}
                                                    </div>
                                                    {convo.unread_traveler > 0 && (
                                                        <span className="absolute -top-1 -right-1 min-w-[16px] h-[16px] px-1 bg-emerald-500 text-white text-[9px] font-bold rounded-full flex items-center justify-center">
                                                            {convo.unread_traveler}
                                                        </span>
                                                    )}
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <div className="flex items-center justify-between">
                                                        <p className="font-semibold text-neutral-900 text-sm truncate">{convo.operator_name}</p>
                                                        <span className="text-[10px] text-neutral-400 shrink-0 ml-2">{formatDate(convo.last_message_at)}</span>
                                                    </div>
                                                    <p className="text-[11px] text-neutral-400 mt-0.5 truncate">{convo.trip_summary}</p>
                                                    <p className="text-xs text-neutral-500 mt-0.5 truncate">{convo.last_message}</p>
                                                </div>
                                            </button>
                                        ))
                                    )}
                                </div>
                            </div>

                            {/* Right: Chat Area — full width on mobile */}
                            <div className={`${!activeConversation ? 'hidden md:flex' : 'flex'} flex-1 flex-col min-w-0`}>
                                {!activeConversation ? (
                                    <div className="flex-1 flex items-center justify-center bg-neutral-50/50">
                                        <div className="text-center">
                                            <div className="w-16 h-16 rounded-2xl bg-neutral-100 flex items-center justify-center mx-auto mb-3">
                                                <MessageCircle size={28} className="text-neutral-300" />
                                            </div>
                                            <p className="text-sm font-medium text-neutral-500">Select a conversation</p>
                                            <p className="text-xs text-neutral-400 mt-1">Choose from your conversations on the left</p>
                                        </div>
                                    </div>
                                ) : (
                                    <>
                                        {/* Chat Header */}
                                        <div className="flex items-center gap-3 px-4 sm:px-5 py-3 border-b border-neutral-100 bg-white shrink-0">
                                            <button onClick={() => setActiveConversation(null)} className="md:hidden p-1 -ml-1 text-neutral-500 hover:text-neutral-900" data-testid="chat-back-btn">
                                                <ChevronLeft size={20} />
                                            </button>
                                            <div className="relative">
                                                <div className="w-10 h-10 rounded-2xl bg-neutral-900 flex items-center justify-center text-white text-sm font-bold">
                                                    {activeConversation.operator_name?.charAt(0)?.toUpperCase() || 'O'}
                                                </div>
                                                <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-emerald-500 border-2 border-white rounded-full" />
                                            </div>
                                            <div className="min-w-0 flex-1">
                                                <p className="font-semibold text-neutral-900 text-sm">{activeConversation.operator_name}</p>
                                                <p className="text-[11px] text-neutral-400 truncate">{activeConversation.trip_summary}</p>
                                            </div>
                                        </div>

                                        {/* Messages */}
                                        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3 bg-neutral-50/30" data-testid="chat-messages">
                                            {messages.map((msg, idx) => {
                                                const isMe = msg.sender_id === user?.id;
                                                const isSystem = msg.sender_role === 'system';
                                                const showTime = idx === 0 || (new Date(msg.created_at) - new Date(messages[idx - 1]?.created_at)) > 300000;
                                                return (
                                                    <div key={msg.id}>
                                                        {showTime && (
                                                            <div className="text-center my-3">
                                                                <span className="text-[10px] text-neutral-400 bg-white px-3 py-1 rounded-full border border-neutral-100">{formatTime(msg.created_at)}</span>
                                                            </div>
                                                        )}
                                                        <div className={`flex ${isSystem ? 'justify-center' : isMe ? 'justify-end' : 'justify-start'}`}>
                                                            {isSystem ? (
                                                                <div className="bg-white text-neutral-500 text-xs px-4 py-2 rounded-full max-w-md text-center border border-neutral-100">{msg.content}</div>
                                                            ) : (
                                                                <div className="max-w-[70%]">
                                                                    <div className={`px-4 py-2.5 rounded-2xl text-sm leading-relaxed ${
                                                                        isMe ? 'bg-neutral-900 text-white rounded-br-md' : 'bg-white text-neutral-900 rounded-bl-md border border-neutral-100'
                                                                    }`} data-testid={`msg-${msg.id}`}>
                                                                        <p>{msg.content}</p>
                                                                    </div>
                                                                    <div className={`flex items-center gap-1.5 mt-1 px-1 ${isMe ? 'justify-end' : 'justify-start'}`}>
                                                                        <span className="text-[10px] text-neutral-400">{formatTime(msg.created_at)}</span>
                                                                        {isMe && <span className="text-[10px] text-neutral-400 flex items-center"><Check size={10} className="text-emerald-500" /><Check size={10} className="-ml-1.5 text-emerald-500" /></span>}
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                            <div ref={messagesEndRef} />
                                        </div>

                                        {/* Input */}
                                        <div className="px-5 py-3 border-t border-neutral-100 bg-white shrink-0">
                                            <div className="flex items-end gap-2">
                                                <div className="flex-1 bg-neutral-50 border border-neutral-200 rounded-2xl px-4 py-2.5 focus-within:border-neutral-400 focus-within:bg-white transition">
                                                    <Input
                                                        value={newMessage}
                                                        onChange={e => setNewMessage(e.target.value)}
                                                        onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); } }}
                                                        placeholder="Type a message..."
                                                        className="border-0 p-0 h-auto bg-transparent shadow-none focus-visible:ring-0 text-sm"
                                                        data-testid="chat-input"
                                                    />
                                                </div>
                                                <Button
                                                    onClick={sendMessage}
                                                    disabled={sendingMsg || !newMessage.trim()}
                                                    className="bg-neutral-900 text-white hover:bg-neutral-800 rounded-2xl w-10 h-10 p-0 flex items-center justify-center shrink-0 disabled:opacity-30"
                                                    data-testid="chat-send"
                                                >
                                                    {sendingMsg ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
                                                </Button>
                                            </div>
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                {/* ===== INBOX ===== */}
                {/* ===== TRIPS (combined with Bookings) ===== */}
                {activeView === 'trips' && (
                    <div className="py-8">
                        <SubHeader title="Your Trips" subtitle={`${trips.length} request${trips.length !== 1 ? 's' : ''}, ${bookings.filter(b => b.status === 'confirmed').length} booked`} onBack={() => setActiveView('home')}>
                            <Button onClick={() => navigate('/trip/create')} className="bg-neutral-900 text-white hover:bg-neutral-800 rounded-lg px-4 py-2.5 text-sm font-medium" data-testid="trips-create-btn">
                                <Plus size={16} className="mr-1" /> New Trip
                            </Button>
                        </SubHeader>

                        {/* Status tabs */}
                        <div className="flex gap-1.5 mb-6 flex-wrap" data-testid="trip-status-tabs">
                            {[
                                { id: 'all', label: 'All', count: trips.length + bookings.length },
                                { id: 'planning', label: 'Planning', count: trips.filter(t => ['pending', 'broadcasted'].includes(t.status)).length },
                                { id: 'proposals', label: 'Proposals', count: trips.filter(t => t.status === 'responded').length },
                                { id: 'booked', label: 'Booked', count: bookings.filter(b => b.status === 'confirmed').length },
                                { id: 'cancelled', label: 'Cancelled', count: bookings.filter(b => b.status === 'cancelled').length },
                            ].map(tab => (
                                <button
                                    key={tab.id}
                                    onClick={() => setTripTab(tab.id)}
                                    className={`px-3.5 py-1.5 rounded-full text-xs font-medium border transition-all ${
                                        tripTab === tab.id
                                            ? 'bg-neutral-900 text-white border-neutral-900'
                                            : 'bg-white text-neutral-600 border-neutral-200 hover:border-neutral-400'
                                    }`}
                                    data-testid={`trip-tab-${tab.id}`}
                                >
                                    {tab.label}{tab.count > 0 ? ` (${tab.count})` : ''}
                                </button>
                            ))}
                        </div>

                        {/* Trip Requests */}
                        {(tripTab === 'all' || tripTab === 'planning' || tripTab === 'proposals') && (
                            (() => {
                                const filtered = trips.filter(t => {
                                    if (tripTab === 'planning') return ['pending', 'broadcasted'].includes(t.status);
                                    if (tripTab === 'proposals') return t.status === 'responded';
                                    return true; // 'all'
                                });
                                return filtered.length > 0 ? (
                                    <div className="mb-8">
                                        {tripTab === 'all' && <h3 className="text-sm font-semibold text-neutral-400 uppercase tracking-wider mb-4">Trip Requests</h3>}
                                        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                                            {filtered.map(trip => (
                                                <TripCard key={trip.id} trip={trip} onClick={() => openTrip(trip)} formatDate={formatDate} />
                                            ))}
                                        </div>
                                    </div>
                                ) : tripTab !== 'all' ? (
                                    <EmptyState icon={Map} title="No trips here" desc="No trips match this status." />
                                ) : null;
                            })()
                        )}

                        {/* Bookings */}
                        {(tripTab === 'all' || tripTab === 'booked' || tripTab === 'cancelled') && (
                            (() => {
                                const filtered = bookings.filter(b => {
                                    if (tripTab === 'booked') return b.status === 'confirmed';
                                    if (tripTab === 'cancelled') return b.status === 'cancelled';
                                    return true; // 'all'
                                });
                                return filtered.length > 0 ? (
                                    <div className="mb-8">
                                        {tripTab === 'all' && <h3 className="text-sm font-semibold text-neutral-400 uppercase tracking-wider mb-4">Bookings</h3>}
                                        <div className="space-y-4">
                                            {filtered.map(booking => (
                                                <div key={booking.id} className={`rounded-xl border p-5 transition-all ${booking.status === 'confirmed' ? 'border-[#073763] bg-emerald-50/20' : booking.status === 'cancelled' ? 'border-neutral-200 bg-neutral-50 opacity-60' : 'border-neutral-200'}`} data-testid={`booking-${booking.id}`}>
                                                    <div className="flex items-start justify-between mb-3">
                                                        <div>
                                                            <h4 className="font-semibold text-neutral-900">{booking.itinerary_title}</h4>
                                                            <p className="text-sm text-neutral-400">by {booking.company_name}
                                                                <button onClick={() => previewOperator(booking.operator_id)} className="ml-2 text-blue-500 hover:text-blue-700 inline-flex items-center gap-0.5 text-xs font-medium" data-testid={`view-op-bk-${booking.id}`}>
                                                                    <Eye size={11} /> View Operator
                                                                </button>
                                                            </p>
                                                        </div>
                                                        <Badge className={booking.status === 'confirmed' ? 'bg-[#073763] text-white' : booking.status === 'cancelled' ? 'bg-neutral-200 text-neutral-500' : 'bg-blue-50 text-blue-700'}>
                                                            {booking.status === 'confirmed' && <CircleCheck size={12} className="mr-1" />}
                                                            {booking.status}
                                                        </Badge>
                                                    </div>
                                                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
                                                        <div className="bg-neutral-50 rounded-lg p-3">
                                                            <p className="text-xs text-neutral-400">Ref</p>
                                                            <p className="text-sm font-mono font-semibold text-neutral-900">{booking.booking_ref}</p>
                                                        </div>
                                                        <div className="bg-neutral-50 rounded-lg p-3">
                                                            <p className="text-xs text-neutral-400">Total</p>
                                                            <p className="text-sm font-semibold text-neutral-900">${booking.total_price}</p>
                                                        </div>
                                                        <div className="bg-neutral-50 rounded-lg p-3">
                                                            <p className="text-xs text-neutral-400">Duration</p>
                                                            <p className="text-sm font-semibold text-neutral-900">{booking.duration_days} days</p>
                                                        </div>
                                                        <div className="bg-neutral-50 rounded-lg p-3">
                                                            <p className="text-xs text-neutral-400">Booked</p>
                                                            <p className="text-sm font-semibold text-neutral-900">{formatDate(booking.created_at)}</p>
                                                        </div>
                                                    </div>
                                                    {booking.traveler_notes && (
                                                        <p className="text-sm text-neutral-500 italic mb-3">"{booking.traveler_notes}"</p>
                                                    )}
                                                    {booking.status === 'confirmed' && (
                                                        <div className="flex items-center gap-3">
                                                            <button onClick={() => { setReviewDialog(booking); setReviewRating(5); setReviewComment(''); }} className="text-sm text-[#073763] hover:text-[#052a4a] font-medium flex items-center gap-1" data-testid={`review-booking-${booking.id}`}>
                                                                <Star size={14} /> Review
                                                            </button>
                                                            <button onClick={() => cancelBooking(booking.id)} className="text-sm text-red-500 hover:text-red-700 font-medium underline underline-offset-2" data-testid={`cancel-booking-${booking.id}`}>
                                                                Cancel booking
                                                            </button>
                                                        </div>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                ) : tripTab !== 'all' ? (
                                    <EmptyState icon={BookOpen} title={tripTab === 'booked' ? 'No bookings yet' : 'No cancelled trips'} desc={tripTab === 'booked' ? 'Book an itinerary from your trip proposals.' : ''} />
                                ) : null;
                            })()
                        )}

                        {/* Empty state for all */}
                        {trips.length === 0 && bookings.length === 0 && (
                            <EmptyState icon={Map} title="No trips yet" desc="Start planning your Tanzania adventure." actionLabel="Create your first trip" onAction={() => navigate('/trip/create')} />
                        )}
                    </div>
                )}

                {/* ===== TRIP DETAIL ===== */}
                {activeView === 'trip-detail' && selectedTrip && (
                    <div className="py-8 max-w-5xl">
                        <SubHeader
                            title={selectedTrip.destination_interests?.join(', ') || 'Trip Details'}
                            subtitle={`${formatDate(selectedTrip.travel_dates?.start)} — ${formatDate(selectedTrip.travel_dates?.end)}`}
                            onBack={() => { setActiveView('trips'); setCompareMode(false); setCompareItems([]); }}
                        />

                        {/* Trip Snapshot - matching wizard review style */}
                        <div className="bg-white rounded-2xl border border-neutral-200 overflow-hidden mb-8" data-testid="trip-snapshot">
                            <div className="bg-neutral-900 p-6">
                                <p className="text-base font-semibold text-white leading-relaxed">
                                    {selectedTrip.broadcast_summary || `${selectedTrip.destination_interests?.join(', ')} trip for ${selectedTrip.group_size} traveler${selectedTrip.group_size !== 1 ? 's' : ''}`}
                                </p>
                                <div className="flex items-center gap-3 mt-3">
                                    <Badge className={`${statusColors[selectedTrip.status]} border-0`}>{selectedTrip.status}</Badge>
                                    <span className="text-xs text-neutral-400">Submitted {formatDate(selectedTrip.created_at)}</span>
                                </div>
                            </div>
                            <div className="p-6">
                                <div className="space-y-0">
                                    {[
                                        { icon: MapPin, label: 'Destinations', value: [...(selectedTrip.specific_destinations || []), ...(selectedTrip.destination_interests || [])].join(', ') || 'Open to suggestions' },
                                        { icon: Calendar, label: 'Travel Dates', value: selectedTrip.travel_dates?.start ? `${formatDate(selectedTrip.travel_dates.start)} — ${formatDate(selectedTrip.travel_dates.end)}` : '—' },
                                        { icon: DollarSign, label: 'Budget', value: selectedTrip.budget_range ? `$${selectedTrip.budget_range.min?.toLocaleString()} — $${selectedTrip.budget_range.max?.toLocaleString()} per person` : '—' },
                                        { icon: Users, label: 'Group Size', value: `${selectedTrip.group_size} traveler${selectedTrip.group_size !== 1 ? 's' : ''}` },
                                    ].map((item, i) => (
                                        <div key={i} className="flex items-center justify-between py-3 border-b border-neutral-100 last:border-0">
                                            <div className="flex items-center gap-3">
                                                <item.icon size={16} className="text-neutral-400" />
                                                <span className="text-sm text-neutral-500">{item.label}</span>
                                            </div>
                                            <p className="text-sm font-medium text-neutral-900 text-right max-w-[55%]">{item.value}</p>
                                        </div>
                                    ))}
                                    {selectedTrip.preferences?.length > 0 && (
                                        <div className="flex items-start justify-between py-3 border-b border-neutral-100">
                                            <div className="flex items-center gap-3 shrink-0">
                                                <Check size={16} className="text-neutral-400" />
                                                <span className="text-sm text-neutral-500">Preferences</span>
                                            </div>
                                            <div className="flex flex-wrap gap-1.5 justify-end max-w-[55%]">
                                                {selectedTrip.preferences.map(p => (
                                                    <span key={p} className="text-xs bg-neutral-100 text-neutral-600 px-2.5 py-1 rounded-full">{p}</span>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                    {selectedTrip.special_requests && (
                                        <div className="py-3">
                                            <p className="text-sm text-neutral-500 mb-1">Special Requests</p>
                                            <p className="text-sm text-neutral-700 italic">"{selectedTrip.special_requests}"</p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Proposals Section */}
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="font-semibold text-neutral-900">{itineraries.length} Proposal{itineraries.length !== 1 ? 's' : ''} Received</h3>
                            {itineraries.length >= 2 && (
                                <Button
                                    onClick={() => { setCompareMode(!compareMode); setCompareItems([]); }}
                                    className={`text-sm rounded-lg px-4 py-2 font-medium ${compareMode ? 'bg-neutral-900 text-white hover:bg-neutral-800' : 'bg-white border border-neutral-200 text-neutral-700 hover:bg-neutral-50'}`}
                                    data-testid="compare-toggle"
                                >
                                    <ArrowLeftRight size={15} className="mr-1.5" />
                                    {compareMode ? 'Exit Compare' : 'Compare'}
                                </Button>
                            )}
                        </div>

                        {loadingItineraries ? (
                            <div className="flex justify-center py-16"><div className="spinner" /></div>
                        ) : itineraries.length === 0 ? (
                            <div className="text-center py-16 bg-neutral-50 rounded-2xl">
                                <Clock className="mx-auto mb-3 text-neutral-300" size={36} />
                                <p className="font-medium text-neutral-900 mb-1">Waiting for responses</p>
                                <p className="text-sm text-neutral-400 max-w-xs mx-auto">Operators are reviewing your trip. Proposals will show up here.</p>
                            </div>
                        ) : compareMode && compareItems.length >= 2 ? (
                            /* ===== COMPARISON VIEW ===== */
                            <div data-testid="comparison-view">
                                <p className="text-sm text-neutral-500 mb-4">Comparing {compareItems.length} itineraries side by side</p>
                                <div className="overflow-x-auto">
                                    <table className="w-full border-collapse min-w-[600px]">
                                        <thead>
                                            <tr>
                                                <th className="text-left text-xs text-neutral-400 font-medium py-3 px-4 w-36 border-b border-neutral-100"></th>
                                                {compareItems.map(it => (
                                                    <th key={it.id} className="text-left py-3 px-4 border-b border-neutral-100">
                                                        <div className="flex items-center justify-between">
                                                            <div>
                                                                <p className="font-semibold text-neutral-900 text-sm">{it.title}</p>
                                                                <p className="text-xs text-neutral-400 font-normal">by {it.company_name}</p>
                                                            </div>
                                                            <button onClick={() => toggleCompare(it)} className="p-1 hover:bg-neutral-100 rounded">
                                                                <X size={14} className="text-neutral-400" />
                                                            </button>
                                                        </div>
                                                    </th>
                                                ))}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {[
                                                { label: 'Total Price', render: it => <span className="text-lg font-bold text-[#073763]">${it.total_price}</span> },
                                                { label: 'Per Person', render: it => <span className="font-semibold">${Math.round(it.total_price / (selectedTrip?.group_size || 1))}</span> },
                                                { label: 'Duration', render: it => `${it.duration_days} days` },
                                                { label: 'Status', render: it => <Badge className={it.status === 'selected' ? 'bg-neutral-900 text-white' : 'bg-neutral-100 text-neutral-600'}>{it.status}</Badge> },
                                                { label: 'Inclusions', render: it => (
                                                    <div className="space-y-1">{it.inclusions?.map((inc, i) => (
                                                        <div key={i} className="flex items-center gap-1.5 text-xs"><Check size={12} className="text-[#073763] shrink-0" />{inc}</div>
                                                    ))}</div>
                                                )},
                                                { label: 'Exclusions', render: it => (
                                                    <div className="space-y-1">{it.exclusions?.map((exc, i) => (
                                                        <div key={i} className="flex items-center gap-1.5 text-xs text-neutral-400"><X size={12} className="shrink-0" />{exc}</div>
                                                    ))}</div>
                                                )},
                                            ].map((row, ri) => (
                                                <tr key={ri} className={ri % 2 === 0 ? 'bg-neutral-50/50' : ''}>
                                                    <td className="text-xs text-neutral-500 font-medium py-3 px-4 align-top border-b border-neutral-50">{row.label}</td>
                                                    {compareItems.map(it => (
                                                        <td key={it.id} className="py-3 px-4 text-sm text-neutral-700 align-top border-b border-neutral-50">{row.render(it)}</td>
                                                    ))}
                                                </tr>
                                            ))}
                                            {Array.from({ length: Math.max(...compareItems.map(it => it.daily_plan?.length || 0)) }, (_, di) => (
                                                <tr key={`day-${di}`} className={di % 2 === 0 ? 'bg-neutral-50/50' : ''}>
                                                    <td className="text-xs text-neutral-500 font-medium py-3 px-4 align-top border-b border-neutral-50">Day {di + 1}</td>
                                                    {compareItems.map(it => {
                                                        const day = it.daily_plan?.[di];
                                                        return (
                                                            <td key={it.id} className="py-3 px-4 text-sm text-neutral-700 align-top border-b border-neutral-50">
                                                                {day ? (
                                                                    <div>
                                                                        <p className="text-xs font-medium text-neutral-900">{day.accommodation || 'N/A'}</p>
                                                                        <p className="text-xs text-neutral-400 mt-0.5">{Array.isArray(day.activities) ? day.activities.join(', ') : day.activities}</p>
                                                                    </div>
                                                                ) : <span className="text-neutral-300">—</span>}
                                                            </td>
                                                        );
                                                    })}
                                                </tr>
                                            ))}
                                            <tr>
                                                <td className="py-4 px-4"></td>
                                                {compareItems.map(it => (
                                                    <td key={it.id} className="py-4 px-4">
                                                        {it.status === 'submitted' && selectedTrip?.status !== 'booked' && (
                                                            <Button onClick={() => openBookingFlow(it)} className="w-full bg-[#073763] text-white hover:bg-[#052a4a] rounded-lg py-2.5 font-medium text-sm" data-testid={`compare-book-${it.id}`}>
                                                                Book This
                                                            </Button>
                                                        )}
                                                    </td>
                                                ))}
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        ) : (
                            /* ===== PROPOSAL CARDS (enhanced to match operator view) ===== */
                            <div className="space-y-5">
                                {compareMode && compareItems.length < 2 && (
                                    <div className="bg-blue-50 text-blue-700 text-sm rounded-xl px-4 py-3 flex items-center gap-2">
                                        <AlertCircle size={16} /> Select {2 - compareItems.length} more itinerar{compareItems.length === 1 ? 'y' : 'ies'} to compare
                                    </div>
                                )}
                                {itineraries.map(it => (
                                    <div key={it.id} className={`bg-white rounded-2xl border overflow-hidden transition-all ${
                                        it.status === 'selected' ? 'border-emerald-300' : compareItems.find(c => c.id === it.id) ? 'border-blue-400' : 'border-neutral-200'
                                    }`} data-testid={`itinerary-${it.id}`}>
                                        {/* Proposal Header (dark) */}
                                        <div className="bg-neutral-900 p-5 flex items-start justify-between">
                                            <div className="flex items-start gap-3">
                                                {compareMode && (
                                                    <button onClick={() => toggleCompare(it)} className={`mt-0.5 w-5 h-5 rounded border-2 flex items-center justify-center transition shrink-0 ${compareItems.find(c => c.id === it.id) ? 'border-blue-500 bg-blue-500' : 'border-white/30'}`} data-testid={`compare-cb-${it.id}`}>
                                                        {compareItems.find(c => c.id === it.id) && <Check size={12} className="text-white" />}
                                                    </button>
                                                )}
                                                <div>
                                                    <h4 className="font-bold text-white text-base">{it.title}</h4>
                                                    <p className="text-sm text-neutral-300 mt-0.5">by {it.company_name}
                                                        <button onClick={() => previewOperator(it.operator_id)} className="ml-2 text-blue-300 hover:text-blue-100 inline-flex items-center gap-0.5 text-xs font-medium" data-testid={`view-op-${it.id}`}>
                                                            <Eye size={11} /> View Profile
                                                        </button>
                                                    </p>
                                                    <p className="text-neutral-400 text-sm mt-0.5">{it.duration_days} days &middot; ${parseFloat(it.total_price || 0).toLocaleString()} {it.currency || 'USD'}</p>
                                                    {it.price_per_person && <p className="text-xs text-neutral-500">${parseFloat(it.price_per_person).toLocaleString()} per person</p>}
                                                </div>
                                            </div>
                                            <Badge className={`${it.status === 'selected' ? 'bg-emerald-500/20 text-emerald-300' : 'bg-white/10 text-neutral-300'} border-0`}>
                                                {it.status === 'selected' && <Check size={11} className="mr-1" />}{it.status}
                                            </Badge>
                                        </div>

                                        {/* Proposal Content */}
                                        <div className="p-5 space-y-5">
                                            {it.description && (
                                                <div>
                                                    <p className="text-[10px] uppercase tracking-widest text-neutral-400 font-bold mb-1.5">Overview</p>
                                                    <p className="text-sm text-neutral-700 leading-relaxed">{it.description}</p>
                                                </div>
                                            )}

                                            {/* Highlights */}
                                            {it.highlights?.filter(h => h?.trim()).length > 0 && (
                                                <div>
                                                    <p className="text-[10px] uppercase tracking-widest text-neutral-400 font-bold mb-2">Highlights</p>
                                                    <div className="space-y-1.5">
                                                        {it.highlights.filter(h => h?.trim()).map((h, i) => (
                                                            <div key={i} className="flex items-start gap-2">
                                                                <Star size={12} className="text-amber-400 mt-0.5 shrink-0" />
                                                                <span className="text-sm text-neutral-700">{h}</span>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}

                                            {/* Day Plan */}
                                            {it.daily_plan?.length > 0 && (
                                                <div>
                                                    <p className="text-[10px] uppercase tracking-widest text-neutral-400 font-bold mb-3">Day-by-Day Itinerary</p>
                                                    <div className="space-y-3">
                                                        {it.daily_plan.map((day, idx) => (
                                                            <div key={idx} className="flex gap-4" data-testid={`proposal-day-${idx}`}>
                                                                <div className="flex flex-col items-center">
                                                                    <div className="w-7 h-7 rounded-lg bg-neutral-900 text-white text-xs font-bold flex items-center justify-center shrink-0">{day.day || idx + 1}</div>
                                                                    {idx < it.daily_plan.length - 1 && <div className="w-px flex-1 bg-neutral-200 mt-1" />}
                                                                </div>
                                                                <div className="flex-1 pb-3">
                                                                    <p className="text-sm font-semibold text-neutral-900">{day.title || `Day ${day.day || idx + 1}`}</p>
                                                                    {day.activities && <p className="text-xs text-neutral-600 mt-0.5">{Array.isArray(day.activities) ? day.activities.join(', ') : day.activities}</p>}
                                                                    <div className="flex flex-wrap gap-3 mt-1.5 text-[11px] text-neutral-400">
                                                                        {day.accommodation && <span>{day.accommodation}</span>}
                                                                        {day.transport && <span>&middot; {day.transport}</span>}
                                                                        {day.meals && <span>&middot; {Array.isArray(day.meals) ? day.meals.join(', ') : day.meals}</span>}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}

                                            {/* Logistics */}
                                            {(it.accommodation_type || it.transport_type) && (
                                                <div className="grid sm:grid-cols-2 gap-4">
                                                    {it.accommodation_type && (
                                                        <div>
                                                            <p className="text-[10px] uppercase tracking-widest text-neutral-400 font-bold mb-1.5">Accommodation</p>
                                                            <p className="text-sm text-neutral-700 capitalize">{it.accommodation_type}</p>
                                                            {it.lodge_names?.filter(n => n?.trim()).length > 0 && (
                                                                <p className="text-xs text-neutral-500 mt-0.5">{it.lodge_names.filter(n => n?.trim()).join(', ')}</p>
                                                            )}
                                                        </div>
                                                    )}
                                                    {it.transport_type && (
                                                        <div>
                                                            <p className="text-[10px] uppercase tracking-widest text-neutral-400 font-bold mb-1.5">Transport</p>
                                                            <p className="text-sm text-neutral-700">{it.transport_type}</p>
                                                            <div className="flex gap-3 mt-1 text-xs text-neutral-500">
                                                                {it.guide_included && <span>Guide included</span>}
                                                                {it.transfers_included && <span>Transfers included</span>}
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            )}

                                            {/* Inclusions / Exclusions */}
                                            <div className="grid sm:grid-cols-2 gap-4">
                                                {it.inclusions?.length > 0 && (
                                                    <div>
                                                        <p className="text-[10px] uppercase tracking-widest text-neutral-400 font-bold mb-2">Included</p>
                                                        <div className="space-y-1">
                                                            {it.inclusions.map(item => (
                                                                <div key={item} className="flex items-center gap-2 text-sm text-neutral-700">
                                                                    <Check size={12} className="text-emerald-500 shrink-0" /> {item}
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                )}
                                                {it.exclusions?.length > 0 && (
                                                    <div>
                                                        <p className="text-[10px] uppercase tracking-widest text-neutral-400 font-bold mb-2">Excluded</p>
                                                        <div className="space-y-1">
                                                            {it.exclusions.map(item => (
                                                                <div key={item} className="flex items-center gap-2 text-sm text-neutral-500">
                                                                    <X size={12} className="text-red-400 shrink-0" /> {item}
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>

                                            {/* Book Action */}
                                            {it.status === 'submitted' && selectedTrip?.status !== 'booked' && !compareMode && (
                                                <Button onClick={() => openBookingFlow(it)} className="w-full bg-neutral-900 text-white hover:bg-neutral-800 rounded-xl py-3 font-semibold text-sm" data-testid={`book-itinerary-${it.id}`}>
                                                    <BookOpen size={16} className="mr-2" /> Book This Itinerary
                                                </Button>
                                            )}
                                            {it.status === 'selected' && (
                                                <div className="flex items-center gap-2 text-emerald-600 text-sm font-semibold bg-emerald-50 rounded-xl px-4 py-3">
                                                    <CircleCheck size={16} /> You've selected this itinerary
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}

                {/* ===== BOOKING SUCCESS ===== */}
                {activeView === 'booking-success' && selectedBooking && (
                    <div className="py-12 max-w-lg mx-auto text-center" data-testid="booking-success">
                        <div className="w-20 h-20 rounded-full bg-[#073763] flex items-center justify-center mx-auto mb-6">
                            <CircleCheck size={40} className="text-white" />
                        </div>
                        <h1 className="text-2xl font-bold text-neutral-900 mb-2">Booking Confirmed!</h1>
                        <p className="text-neutral-500 mb-8">Your trip has been booked successfully. The operator will contact you soon.</p>

                        <div className="bg-white border border-neutral-200 rounded-xl p-6 text-left space-y-4 mb-8">
                            <div className="flex justify-between items-center pb-3 border-b border-neutral-100">
                                <span className="text-sm text-neutral-400">Booking Reference</span>
                                <span className="font-mono font-bold text-lg text-[#073763]">{selectedBooking.booking_ref}</span>
                            </div>
                            <div className="flex justify-between text-sm">
                                <span className="text-neutral-400">Itinerary</span>
                                <span className="font-medium text-neutral-900">{selectedBooking.itinerary_title}</span>
                            </div>
                            <div className="flex justify-between text-sm">
                                <span className="text-neutral-400">Operator</span>
                                <span className="font-medium text-neutral-900">{selectedBooking.company_name}</span>
                            </div>
                            <div className="flex justify-between text-sm">
                                <span className="text-neutral-400">Duration</span>
                                <span className="font-medium text-neutral-900">{selectedBooking.duration_days} days</span>
                            </div>
                            <div className="flex justify-between text-sm pt-3 border-t border-neutral-100">
                                <span className="text-neutral-400">Total Price</span>
                                <span className="font-bold text-lg text-neutral-900">${selectedBooking.total_price}</span>
                            </div>
                        </div>

                        <div className="flex gap-3 justify-center">
                            <Button onClick={() => { setTripTab('booked'); setActiveView('trips'); }} className="bg-neutral-900 text-white hover:bg-neutral-800 rounded-lg px-6 py-3 font-medium" data-testid="view-bookings-btn">
                                View My Bookings
                            </Button>
                            <Button onClick={() => setActiveView('home')} className="bg-white border border-neutral-200 text-neutral-700 hover:bg-neutral-50 rounded-lg px-6 py-3 font-medium">
                                Back to Home
                            </Button>
                        </div>
                    </div>
                )}


                {/* ===== CALENDAR ===== */}
                {activeView === 'calendar' && (
                    <div className="py-4" data-testid="calendar-view">
                        <h2 className="text-lg font-semibold text-neutral-900 mb-4">Upcoming Schedule</h2>
                        {(() => {
                            const upcomingTrips = trips.filter(t => {
                                if (!t.travel_dates) return false;
                                try { return new Date(t.travel_dates) >= new Date(); } catch { return false; }
                            }).sort((a, b) => new Date(a.travel_dates) - new Date(b.travel_dates));
                            const confirmedBookings = bookings.filter(b => b.status === 'confirmed');
                            const hasEvents = upcomingTrips.length > 0 || confirmedBookings.length > 0;
                            
                            if (!hasEvents) return (
                                <div className="text-center py-16 bg-neutral-50 rounded-2xl">
                                    <CalendarDays className="mx-auto mb-3 text-neutral-300" size={36} />
                                    <p className="font-medium text-neutral-900 mb-1">No upcoming events</p>
                                    <p className="text-sm text-neutral-400 max-w-xs mx-auto">Your trip dates and confirmed bookings will appear here.</p>
                                    <Button onClick={() => navigate('/trip/create')} className="mt-4 bg-neutral-900 text-white hover:bg-neutral-800 rounded-lg px-5 py-2.5 text-sm font-medium">
                                        <Plus size={15} className="mr-1.5" /> Plan a Trip
                                    </Button>
                                </div>
                            );
                            
                            return (
                                <div className="space-y-3">
                                    {confirmedBookings.map(bk => (
                                        <div key={bk.id} className="bg-white rounded-xl border border-neutral-200 p-4 flex items-center gap-4" data-testid={`cal-booking-${bk.id}`}>
                                            <div className="w-12 h-12 rounded-xl bg-emerald-50 flex items-center justify-center">
                                                <Check size={20} className="text-emerald-600" />
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <p className="font-medium text-neutral-900 text-sm">{bk.itinerary_title || 'Confirmed Booking'}</p>
                                                <p className="text-xs text-neutral-400 mt-0.5">{bk.operator_name} &middot; Ref: {bk.booking_ref}</p>
                                            </div>
                                            <Badge className="bg-emerald-50 text-emerald-700 text-xs">Confirmed</Badge>
                                        </div>
                                    ))}
                                    {upcomingTrips.map(trip => (
                                        <div key={trip.id} className="bg-white rounded-xl border border-neutral-200 p-4 flex items-center gap-4" data-testid={`cal-trip-${trip.id}`}>
                                            <div className="w-12 h-12 rounded-xl bg-blue-50 flex items-center justify-center">
                                                <Calendar size={20} className="text-blue-600" />
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <p className="font-medium text-neutral-900 text-sm">{trip.destination_interests?.join(', ') || 'Trip'}</p>
                                                <p className="text-xs text-neutral-400 mt-0.5">{formatDate(trip.travel_dates)} &middot; {trip.group_size} travelers</p>
                                            </div>
                                            <Badge className={`text-xs ${trip.status === 'responded' ? 'bg-blue-50 text-blue-700' : 'bg-neutral-100 text-neutral-600'}`}>{trip.status}</Badge>
                                        </div>
                                    ))}
                                </div>
                            );
                        })()}
                    </div>
                )}


                {/* ===== BUCKETLIST ===== */}
                {activeView === 'bucketlist' && (
                    <div className="py-8">
                        <SubHeader title="Your bucketlist" subtitle={`${bucketlist.length} saved`} onBack={() => setActiveView('home')} />
                        {bucketlist.length === 0 ? (
                            <EmptyState icon={Heart} title="Nothing saved yet" desc="Tap the heart on any experience to save it here." actionLabel="Explore experiences" onAction={() => navigate('/experiences')} />
                        ) : (
                            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-6 gap-y-8">
                                {bucketlist.map(item => (
                                    <div key={item.id} className="group" data-testid={`bucketlist-${item.id}`}>
                                        <div className="relative aspect-square rounded-xl overflow-hidden mb-3">
                                            <img src={item.image} alt={item.title} className="w-full h-full object-cover" />
                                            <button
                                                onClick={() => toggleBucketlist(item)}
                                                className="absolute top-3 right-3 p-1.5"
                                            >
                                                <Heart size={22} className="text-red-500 fill-red-500 drop-shadow-md" strokeWidth={2} />
                                            </button>
                                        </div>
                                        <h4 className="font-medium text-[15px] text-neutral-900">{item.title}</h4>
                                        <p className="text-sm text-neutral-500">{item.tag} &middot; {item.days} days</p>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}

                {/* ===== PROFILE ===== */}
                {activeView === 'profile' && (
                    <div className="py-8">
                        <SubHeader title="Profile" onBack={() => setActiveView('home')} />

                        {!editing ? (
                            <div className="grid lg:grid-cols-3 gap-6 items-stretch">
                                {/* Column 1: Hero Card */}
                                <div className="flex flex-col">
                                    <div className="bg-gradient-to-br from-neutral-900 to-neutral-800 rounded-2xl p-6 relative overflow-hidden flex-1 flex flex-col" data-testid="profile-hero">
                                        <div className="absolute top-0 right-0 w-36 h-36 bg-[#073763]/10 rounded-full -translate-y-1/2 translate-x-1/4" />
                                        <div className="absolute bottom-0 left-0 w-24 h-24 bg-[#073763]/5 rounded-full translate-y-1/2 -translate-x-1/4" />
                                        <div className="relative text-center flex-1 flex flex-col justify-center">
                                            <div className="relative inline-block mb-4 mx-auto">
                                                {getAvatarUrl() ? (
                                                    <img src={getAvatarUrl()} alt="Profile" className="w-20 h-20 rounded-full object-cover ring-4 ring-white/10" />
                                                ) : (
                                                    <div className="w-20 h-20 rounded-full bg-[#073763] flex items-center justify-center text-white text-3xl font-bold ring-4 ring-white/10">
                                                        {(profile?.name || user?.name || 'T').charAt(0).toUpperCase()}
                                                    </div>
                                                )}
                                                <label className={`absolute -bottom-1 -right-1 w-7 h-7 rounded-full bg-white flex items-center justify-center cursor-pointer hover:bg-neutral-100 transition shadow-md ${uploadingAvatar ? 'opacity-50 pointer-events-none' : ''}`}>
                                                    <Camera size={13} className="text-neutral-600" />
                                                    <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={uploadAvatar} data-testid="avatar-upload" />
                                                </label>
                                            </div>
                                            <h2 className="text-lg font-bold text-white truncate flex items-center justify-center gap-1.5" data-testid="profile-name">
                                                {profile?.name || user?.name || 'Traveler'}
                                                {profile?.is_verified && <BadgeCheck size={18} className="text-[#073763] shrink-0" data-testid="verified-badge" />}
                                            </h2>
                                            <p className="text-sm text-neutral-400 mt-0.5 capitalize">{profile?.role || user?.role || 'Traveler'}</p>
                                            {profile?.nationality && (
                                                <span className="inline-flex items-center gap-1.5 text-xs text-white/60 mt-2 mx-auto">
                                                    <Flag size={12} /> {profile.nationality}
                                                </span>
                                            )}
                                            <button
                                                onClick={startEditing}
                                                className="mt-4 flex items-center gap-1.5 bg-white/10 hover:bg-white/20 text-white text-sm font-medium px-4 py-2 rounded-lg transition mx-auto"
                                                data-testid="edit-profile-btn"
                                            >
                                                <Pencil size={14} /> Edit
                                            </button>
                                        </div>

                                        {/* Stats row */}
                                        <div className="relative grid grid-cols-3 gap-3 mt-6 pt-5 border-t border-white/10">
                                            {[
                                                { val: trips.length, label: 'Trips', icon: Map },
                                                { val: (() => { const created = profile?.created_at || user?.created_at; if (!created) return '<1'; const years = Math.floor((Date.now() - new Date(created).getTime()) / (365.25 * 24 * 60 * 60 * 1000)); return years < 1 ? '<1' : years; })(), label: 'Years', icon: CalendarDays },
                                                { val: '4.9', label: 'Rating', icon: Star }
                                            ].map((s, i) => (
                                                <div key={i} className="text-center">
                                                    <p className="text-xl font-bold text-white">{s.val}</p>
                                                    <p className="text-[11px] text-neutral-400 mt-0.5 flex items-center justify-center gap-1"><s.icon size={11} /> {s.label}</p>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>

                                {/* Column 2: Details Card */}
                                <div className="flex flex-col">
                                    <div className="bg-white border border-neutral-200 rounded-2xl divide-y divide-neutral-100 flex-1 flex flex-col" data-testid="profile-details-card">
                                        {[
                                            { icon: UserCircle, label: 'Full name', value: profile?.name || user?.name || '-' },
                                            { icon: Mail, label: 'Email', value: profile?.email || user?.email || 'Not provided' },
                                            { icon: Phone, label: 'Phone', value: `+${profile?.phone || user?.phone || '-'}` },
                                            { icon: Flag, label: 'Nationality', value: profile?.nationality || 'Not provided' },
                                        ].map((row, i) => (
                                            <div key={i} className="flex items-center gap-3 px-5 py-4">
                                                <div className="w-9 h-9 rounded-lg bg-neutral-100 flex items-center justify-center shrink-0"><row.icon size={18} className="text-neutral-500" /></div>
                                                <div className="min-w-0">
                                                    <p className="text-xs text-neutral-400">{row.label}</p>
                                                    <p className="text-sm font-medium text-neutral-900 truncate">{row.value}</p>
                                                </div>
                                            </div>
                                        ))}
                                        <div className="flex items-center gap-3 px-5 py-4">
                                            <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${profile?.is_verified ? 'bg-emerald-50' : 'bg-neutral-100'}`}>
                                                <Shield size={18} className={profile?.is_verified ? 'text-[#073763]' : 'text-neutral-500'} />
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <p className="text-xs text-neutral-400">ID / Passport</p>
                                                {profile?.is_verified ? (
                                                    <p className="text-sm font-medium text-[#073763] flex items-center gap-1.5">
                                                        <BadgeCheck size={14} /> Verified
                                                    </p>
                                                ) : (
                                                    <label className={`inline-flex items-center gap-1.5 text-sm font-medium text-neutral-900 cursor-pointer hover:text-[#073763] transition ${uploadingId ? 'opacity-50 pointer-events-none' : ''}`}>
                                                        <Upload size={14} /> {uploadingId ? 'Uploading...' : 'Upload ID'}
                                                        <input type="file" accept="image/jpeg,image/png,image/webp,application/pdf" className="hidden" onChange={uploadIdDocument} data-testid="id-upload" />
                                                    </label>
                                                )}
                                            </div>
                                            {profile?.is_verified && getIdDocUrl() && !profile.id_document.endsWith('.pdf') && (
                                                <img src={getIdDocUrl()} alt="ID" className="w-10 h-10 rounded-lg object-cover border border-neutral-200" />
                                            )}
                                        </div>

                                        {/* Verification Banner inline */}
                                        {!profile?.is_verified && (
                                            <div className="px-5 py-4 flex items-start gap-3">
                                                <div className="w-9 h-9 rounded-lg bg-amber-100 flex items-center justify-center shrink-0">
                                                    <Shield size={18} className="text-amber-600" />
                                                </div>
                                                <div>
                                                    <p className="text-xs text-amber-700 font-medium">Verify your identity</p>
                                                    <label className={`inline-flex items-center gap-1 text-xs text-amber-600 cursor-pointer hover:text-amber-800 underline underline-offset-2 mt-1 transition ${uploadingId ? 'opacity-50 pointer-events-none' : ''}`}>
                                                        <Upload size={12} /> {uploadingId ? 'Uploading...' : 'Upload ID document'}
                                                        <input type="file" accept="image/jpeg,image/png,image/webp,application/pdf" className="hidden" onChange={uploadIdDocument} data-testid="id-upload-banner" />
                                                    </label>
                                                </div>
                                            </div>
                                        )}

                                        {/* Spacer to push logout to bottom */}
                                        <div className="flex-1" />
                                        <div className="mt-auto">
                                            <button onClick={handleLogout} className="w-full flex items-center px-5 py-4 hover:bg-red-50 transition-colors text-red-500 text-sm font-medium rounded-b-2xl" data-testid="profile-logout-btn">
                                                <LogOut size={18} className="mr-3" /> Log out
                                            </button>
                                        </div>
                                    </div>
                                </div>

                                {/* Column 3: Bio & About Card */}
                                <div className="flex flex-col">
                                    <div className="bg-white border border-neutral-200 rounded-2xl flex-1 flex flex-col overflow-hidden" data-testid="profile-bio-card">
                                        <div className="px-5 pt-5 pb-3">
                                            <p className="text-xs font-semibold text-neutral-400 uppercase tracking-wider">About</p>
                                        </div>
                                        <div className="px-5 pb-5 flex-1">
                                            {profile?.bio ? (
                                                <p className="text-sm text-neutral-600 leading-relaxed">{profile.bio}</p>
                                            ) : (
                                                <p className="text-sm text-neutral-400 italic leading-relaxed">No bio yet. Tell operators a bit about yourself — your travel style, interests, what excites you about Tanzania.</p>
                                            )}
                                        </div>

                                        <div className="border-t border-neutral-100 px-5 py-4">
                                            <p className="text-xs font-semibold text-neutral-400 uppercase tracking-wider mb-3">Travel Preferences</p>
                                            <div className="flex flex-wrap gap-2">
                                                {(profile?.preferences || ['Safari lover', 'Photography', 'Cultural experiences']).map((pref, i) => (
                                                    <span key={i} className="text-xs bg-neutral-100 text-neutral-600 px-3 py-1.5 rounded-full">{pref}</span>
                                                ))}
                                            </div>
                                        </div>

                                        <div className="border-t border-neutral-100 px-5 py-4 mt-auto">
                                            <p className="text-xs font-semibold text-neutral-400 uppercase tracking-wider mb-3">Member Since</p>
                                            <p className="text-sm font-medium text-neutral-700">
                                                {(() => { const d = profile?.created_at || user?.created_at; return d ? new Date(d).toLocaleDateString('en-US', { month: 'long', year: 'numeric' }) : 'Recently joined'; })()}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ) : (
                            /* Edit Mode */
                            <div className="max-w-xl">
                                <div className="flex items-center justify-between mb-6">
                                    <h3 className="text-lg font-semibold text-neutral-900">Edit profile</h3>
                                    <button onClick={() => setEditing(false)} className="p-2 hover:bg-neutral-100 rounded-full transition">
                                        <X size={18} className="text-neutral-500" />
                                    </button>
                                </div>

                                <div className="bg-white border border-neutral-200 rounded-2xl p-6 space-y-5">
                                    <div>
                                        <Label className="text-sm font-medium text-neutral-700 mb-1.5 block">Full name</Label>
                                        <Input
                                            value={editForm.name}
                                            onChange={e => setEditForm(p => ({ ...p, name: e.target.value }))}
                                            className="rounded-lg border-neutral-200 focus:border-neutral-400 focus:ring-0 py-3"
                                            placeholder="Your full name"
                                            data-testid="edit-name"
                                        />
                                    </div>

                                    <div>
                                        <Label className="text-sm font-medium text-neutral-700 mb-1.5 block">Email address</Label>
                                        <Input
                                            type="email"
                                            value={editForm.email}
                                            onChange={e => setEditForm(p => ({ ...p, email: e.target.value }))}
                                            className="rounded-lg border-neutral-200 focus:border-neutral-400 focus:ring-0 py-3"
                                            placeholder="your@email.com"
                                            data-testid="edit-email"
                                        />
                                    </div>

                                    <div>
                                        <Label className="text-sm font-medium text-neutral-700 mb-1.5 block">Nationality</Label>
                                        <Select value={editForm.nationality} onValueChange={v => setEditForm(p => ({ ...p, nationality: v }))}>
                                            <SelectTrigger className="rounded-lg border-neutral-200 py-3" data-testid="edit-nationality">
                                                <SelectValue placeholder="Select nationality" />
                                            </SelectTrigger>
                                            <SelectContent className="max-h-[280px]">
                                                <div className="px-3 py-2 sticky top-0 bg-white border-b z-10">
                                                    <input
                                                        type="text"
                                                        placeholder="Search..."
                                                        value={nationalitySearch}
                                                        onChange={e => setNationalitySearch(e.target.value)}
                                                        className="w-full px-3 py-1.5 text-sm border border-neutral-200 rounded-md focus:outline-none focus:border-neutral-400"
                                                    />
                                                </div>
                                                {NATIONALITIES.filter(n => n.toLowerCase().includes(nationalitySearch.toLowerCase())).map(n => (
                                                    <SelectItem key={n} value={n}>{n}</SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>

                                    <div>
                                        <Label className="text-sm font-medium text-neutral-700 mb-1.5 block">About you</Label>
                                        <textarea
                                            value={editForm.bio}
                                            onChange={e => setEditForm(p => ({ ...p, bio: e.target.value }))}
                                            className="w-full rounded-lg border border-neutral-200 focus:border-neutral-400 focus:ring-0 py-3 px-3 text-sm min-h-[100px] resize-none"
                                            placeholder="Tell us a bit about yourself — your travel style, interests, what excites you about Tanzania..."
                                            data-testid="edit-bio"
                                        />
                                        <p className="text-xs text-neutral-400 mt-1">This helps operators personalize their itineraries for you</p>
                                    </div>

                                    <div>
                                        <Label className="text-sm font-medium text-neutral-700 mb-1.5 block">National ID / Passport</Label>
                                        {profile?.is_verified ? (
                                            <div className="flex items-center gap-3 p-3 bg-emerald-50 rounded-lg border border-emerald-100">
                                                <BadgeCheck size={18} className="text-[#073763] shrink-0" />
                                                <span className="text-sm font-medium text-[#073763]">Verified</span>
                                                {getIdDocUrl() && !profile.id_document.endsWith('.pdf') && (
                                                    <img src={getIdDocUrl()} alt="ID" className="w-8 h-8 rounded object-cover ml-auto border border-emerald-200" />
                                                )}
                                            </div>
                                        ) : (
                                            <label className={`flex items-center justify-center gap-2 p-4 border-2 border-dashed border-neutral-200 rounded-lg cursor-pointer hover:border-neutral-400 transition ${uploadingId ? 'opacity-50 pointer-events-none' : ''}`} data-testid="edit-id-doc">
                                                <Upload size={18} className="text-neutral-400" />
                                                <span className="text-sm text-neutral-500">{uploadingId ? 'Uploading...' : 'Click to upload ID document'}</span>
                                                <input type="file" accept="image/jpeg,image/png,image/webp,application/pdf" className="hidden" onChange={uploadIdDocument} />
                                            </label>
                                        )}
                                        <p className="text-xs text-neutral-400 mt-1">JPEG, PNG, WebP, or PDF — max 10MB</p>
                                    </div>

                                    <div className="flex gap-3 pt-4">
                                        <Button
                                            onClick={saveProfile}
                                            disabled={savingProfile}
                                            className="bg-neutral-900 text-white hover:bg-neutral-800 rounded-lg px-6 py-3 font-medium"
                                            data-testid="save-profile-btn"
                                        >
                                            {savingProfile ? 'Saving...' : 'Save changes'}
                                        </Button>
                                        <Button
                                            onClick={() => setEditing(false)}
                                            className="bg-white border border-neutral-200 text-neutral-700 hover:bg-neutral-50 rounded-lg px-6 py-3 font-medium"
                                        >
                                            Cancel
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </main>

            {/* ===== BOOKING CONFIRMATION DIALOG ===== */}
            <Dialog open={showBookingDialog} onOpenChange={setShowBookingDialog}>
                <DialogContent className="max-w-md" data-testid="booking-dialog">
                    <DialogHeader>
                        <DialogTitle className="text-xl font-bold">Confirm Booking</DialogTitle>
                        <DialogDescription>Review the details and confirm your booking</DialogDescription>
                    </DialogHeader>
                    {bookingItinerary && (
                        <div className="space-y-5 mt-2">
                            <div className="bg-neutral-50 rounded-xl p-4">
                                <h4 className="font-semibold text-neutral-900 mb-1">{bookingItinerary.title}</h4>
                                <p className="text-sm text-neutral-400">by {bookingItinerary.company_name}</p>
                            </div>

                            <div className="grid grid-cols-3 gap-3">
                                <div className="text-center bg-neutral-50 rounded-lg p-3">
                                    <p className="text-xs text-neutral-400">Duration</p>
                                    <p className="font-semibold text-neutral-900">{bookingItinerary.duration_days}d</p>
                                </div>
                                <div className="text-center bg-neutral-50 rounded-lg p-3">
                                    <p className="text-xs text-neutral-400">Total</p>
                                    <p className="font-semibold text-[#073763]">${bookingItinerary.total_price}</p>
                                </div>
                                <div className="text-center bg-neutral-50 rounded-lg p-3">
                                    <p className="text-xs text-neutral-400">Per Person</p>
                                    <p className="font-semibold text-neutral-900">${Math.round(bookingItinerary.total_price / (selectedTrip?.group_size || 1))}</p>
                                </div>
                            </div>

                            {bookingItinerary.inclusions?.length > 0 && (
                                <div>
                                    <p className="text-sm font-medium text-neutral-700 mb-2">Included</p>
                                    <div className="flex flex-wrap gap-1.5">
                                        {bookingItinerary.inclusions.map((inc, i) => (
                                            <span key={i} className="text-xs bg-emerald-50 text-emerald-700 px-2 py-1 rounded-full flex items-center gap-1">
                                                <Check size={10} /> {inc}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            )}

                            <div>
                                <Label className="text-sm font-medium text-neutral-700 mb-1.5 block">Notes for operator (optional)</Label>
                                <textarea
                                    value={bookingNotes}
                                    onChange={e => setBookingNotes(e.target.value)}
                                    className="w-full rounded-lg border border-neutral-200 px-3 py-2.5 text-sm min-h-[80px] resize-none focus:border-neutral-400 focus:ring-0"
                                    placeholder="Any special requests, dietary needs, or questions..."
                                    data-testid="booking-notes"
                                />
                            </div>

                            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                                <p className="text-xs text-amber-700">By confirming, you agree to book this itinerary. Payment details will be coordinated directly with the tour operator.</p>
                            </div>

                            <Button
                                onClick={submitBooking}
                                disabled={bookingSubmitting}
                                className="w-full bg-[#073763] text-white hover:bg-[#052a4a] rounded-lg py-3 font-medium"
                                data-testid="confirm-booking-btn"
                            >
                                {bookingSubmitting ? <Loader2 className="animate-spin mr-2" size={16} /> : <CircleCheck size={16} className="mr-2" />}
                                {bookingSubmitting ? 'Processing...' : `Confirm Booking — $${bookingItinerary.total_price}`}
                            </Button>
                        </div>
                    )}
                </DialogContent>
            </Dialog>

            {/* ===== OPERATOR PREVIEW DIALOG ===== */}
            <Dialog open={!!operatorPreview} onOpenChange={(open) => { if (!open) setOperatorPreview(null); }}>
                <DialogContent className="max-w-md" data-testid="operator-preview-dialog">
                    {operatorPreview && (
                        <>
                            <DialogHeader>
                                <DialogTitle>Operator Profile</DialogTitle>
                            </DialogHeader>
                            <div className="space-y-4 mt-2">
                                <div className="flex items-center gap-4">
                                    <div className="w-14 h-14 rounded-full bg-[#073763]/10 flex items-center justify-center text-xl font-bold text-[#073763] overflow-hidden">
                                        {operatorPreview.avatar_url ? (
                                            <img src={operatorPreview.avatar_url} alt="" className="w-full h-full object-cover" />
                                        ) : (
                                            operatorPreview.company_name?.charAt(0) || operatorPreview.name?.charAt(0) || '?'
                                        )}
                                    </div>
                                    <div>
                                        <p className="font-bold text-zinc-900">{operatorPreview.company_name || operatorPreview.name}</p>
                                        {operatorPreview.company_name && <p className="text-sm text-zinc-500">{operatorPreview.name}</p>}
                                        <div className="flex items-center gap-2 mt-1">
                                            {operatorPreview.is_verified && <Badge className="bg-emerald-50 text-emerald-700 border border-emerald-200 text-[10px]">Verified</Badge>}
                                            {operatorPreview.is_approved && <Badge className="bg-blue-50 text-blue-700 border border-blue-200 text-[10px]">Approved</Badge>}
                                        </div>
                                    </div>
                                </div>
                                <div className="grid grid-cols-2 gap-3">
                                    <div className="bg-zinc-50 rounded-lg p-3 text-center">
                                        <p className="text-lg font-bold text-zinc-900">{operatorPreview.total_proposals || 0}</p>
                                        <p className="text-[10px] text-zinc-400 uppercase tracking-wider">Proposals</p>
                                    </div>
                                    <div className="bg-zinc-50 rounded-lg p-3 text-center">
                                        <p className="text-lg font-bold text-zinc-900">{operatorPreview.total_bookings || 0}</p>
                                        <p className="text-[10px] text-zinc-400 uppercase tracking-wider">Bookings</p>
                                    </div>
                                </div>
                                {operatorPreview.description && (
                                    <div>
                                        <p className="text-xs font-semibold text-zinc-500 uppercase tracking-wider mb-1">About</p>
                                        <p className="text-sm text-zinc-600">{operatorPreview.description}</p>
                                    </div>
                                )}
                                {operatorPreview.services?.length > 0 && (
                                    <div>
                                        <p className="text-xs font-semibold text-zinc-500 uppercase tracking-wider mb-2">Services</p>
                                        <div className="flex flex-wrap gap-1.5">
                                            {operatorPreview.services.map(s => <Badge key={s} className="bg-blue-50 text-blue-700 border border-blue-200 text-xs">{s}</Badge>)}
                                        </div>
                                    </div>
                                )}
                                {operatorPreview.locations?.length > 0 && (
                                    <div>
                                        <p className="text-xs font-semibold text-zinc-500 uppercase tracking-wider mb-2">Locations</p>
                                        <div className="flex flex-wrap gap-1.5">
                                            {operatorPreview.locations.map(l => <Badge key={l} className="bg-zinc-100 text-zinc-700 text-xs">{l}</Badge>)}
                                        </div>
                                    </div>
                                )}
                                {operatorPreview.joined && (
                                    <p className="text-xs text-zinc-400 text-center">Member since {format(new Date(operatorPreview.joined), 'MMM yyyy')}</p>
                                )}
                            </div>
                        </>
                    )}
                </DialogContent>
            </Dialog>

            {/* ===== REVIEW DIALOG ===== */}
            <Dialog open={!!reviewDialog} onOpenChange={(open) => { if (!open) setReviewDialog(null); }}>
                <DialogContent className="max-w-sm" data-testid="review-dialog">
                    <DialogHeader>
                        <DialogTitle>Leave a Review</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 mt-2">
                        <p className="text-sm text-neutral-500">How was your experience with <strong>{reviewDialog?.company_name || 'this operator'}</strong>?</p>
                        <div className="flex gap-1" data-testid="review-stars">
                            {[1,2,3,4,5].map(s => (
                                <button key={s} onClick={() => setReviewRating(s)} className="p-0.5" data-testid={`star-${s}`}>
                                    <Star size={28} className={s <= reviewRating ? 'fill-amber-400 text-amber-400' : 'text-neutral-200'} />
                                </button>
                            ))}
                        </div>
                        <textarea value={reviewComment} onChange={e => setReviewComment(e.target.value)} placeholder="Share your experience (optional)..." rows={3}
                            className="w-full rounded-lg border border-neutral-200 px-3 py-2 text-sm" data-testid="review-comment" />
                        <div className="flex gap-3">
                            <Button onClick={submitReview} disabled={reviewSubmitting} className="flex-1 bg-[#073763] text-white hover:bg-[#052a4a] rounded-lg py-2.5 text-sm font-medium" data-testid="submit-review-btn">
                                {reviewSubmitting ? 'Submitting...' : 'Submit Review'}
                            </Button>
                            <Button onClick={() => setReviewDialog(null)} variant="outline" className="rounded-lg py-2.5 text-sm">Cancel</Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
}

/* ===== Sub Components ===== */

function SubHeader({ title, subtitle, onBack, children }) {
    return (
        <div className="flex items-start justify-between mb-6">
            <div>
                <button onClick={onBack} className="sm:hidden flex items-center gap-1 text-sm text-neutral-500 mb-2"><ChevronLeft size={16} /> Back</button>
                <h2 className="text-2xl font-semibold text-neutral-900">{title}</h2>
                {subtitle && <p className="text-sm text-neutral-400 mt-0.5">{subtitle}</p>}
            </div>
            {children}
        </div>
    );
}

function EmptyState({ icon: Icon, title, desc, actionLabel, onAction }) {
    return (
        <div className="text-center py-20">
            <Icon size={40} className="mx-auto mb-4 text-neutral-300" strokeWidth={1.5} />
            <h3 className="font-semibold text-neutral-900 mb-1">{title}</h3>
            <p className="text-sm text-neutral-400 max-w-xs mx-auto mb-5">{desc}</p>
            {actionLabel && onAction && (
                <Button onClick={onAction} className="bg-neutral-900 text-white hover:bg-neutral-800 rounded-lg px-5 py-2.5 text-sm font-medium">
                    {actionLabel}
                </Button>
            )}
        </div>
    );
}

function ProfileRow({ icon: Icon, label, value, verified }) {
    return (
        <div className="flex items-center justify-between py-4 border-b border-neutral-100">
            <div className="flex items-center gap-3">
                <Icon size={18} className="text-neutral-400" strokeWidth={1.5} />
                <span className="text-sm text-neutral-500">{label}</span>
            </div>
            <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-neutral-900">{value}</span>
                {verified && <Check size={14} className="text-[#073763]" />}
            </div>
        </div>
    );
}


function TripCard({ trip, onClick, formatDate }) {
    return (
        <button onClick={onClick} className="w-full text-left rounded-xl border border-neutral-200 p-5 hover:shadow-md active:scale-[0.99] transition-all" data-testid={`trip-card-${trip.id}`}>
            <div className="flex items-center justify-between mb-3">
                <Badge className={statusColors[trip.status]}>{trip.status}</Badge>
                {trip.itinerary_count > 0 && <span className="text-xs font-semibold text-neutral-900">{trip.itinerary_count} offer{trip.itinerary_count !== 1 ? 's' : ''}</span>}
            </div>
            {trip.broadcast_summary && (
                <p className="text-sm font-medium text-neutral-800 mb-3 leading-relaxed" data-testid={`trip-summary-${trip.id}`}>{trip.broadcast_summary}</p>
            )}
            <div className="flex flex-wrap gap-1.5 mb-3">
                {trip.destination_interests?.slice(0, 3).map(i => <span key={i} className="text-xs bg-neutral-100 text-neutral-600 px-2 py-1 rounded-full">{i}</span>)}
            </div>
            <p className="text-sm text-neutral-500 flex items-center gap-1.5 mb-1">
                <Calendar size={14} /> {formatDate(trip.travel_dates?.start)} — {formatDate(trip.travel_dates?.end)}
            </p>
            <div className="flex items-center justify-between text-sm text-neutral-500">
                <span><Users size={14} className="inline mr-1" />{trip.group_size} guests</span>
                <span className="font-medium text-neutral-900">${trip.budget_range?.min}–${trip.budget_range?.max}</span>
            </div>
        </button>
    );
}
